/**
 * React Query Configuration
 * 
 * Global configuration for @tanstack/react-query. Query keys live in
 * ./queryKeys.ts and invalidation rules in ./invalidation.ts.
 */

import { QueryClient } from '@tanstack/react-query';
import axios from 'axios';

/**
 * Background polling interval for live portfolio data (daily change,
 * holdings summary, allocation). React Query pauses interval refetches
 * while the tab is hidden; refetchOnWindowFocus covers the catch-up.
 */
export const AUTO_REFRESH_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Default query retry policy (WEBD-19): retry once, and only when a retry can
 * help — network failures and 5xx. A 4xx (validation, not found, forbidden,
 * or a 401 the client interceptor already tried to refresh) fails at once.
 */
export function shouldRetryQuery(failureCount: number, error: unknown): boolean {
  if (failureCount >= 1) return false;
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  return status === undefined || status >= 500;
}

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Refetch stale queries when the window regains focus.
      // This re-establishes data after the app has been backgrounded/idle.
      refetchOnWindowFocus: true,
      
      // Retry once, and only network failures and 5xx — never a 4xx (WEBD-19)
      retry: shouldRetryQuery,
      
      // Cache data for 5 minutes
      staleTime: 5 * 60 * 1000,
      
      // Keep unused data in cache for 10 minutes
      gcTime: 10 * 60 * 1000,
    },
    mutations: {
      // Retry failed mutations 0 times
      retry: 0,
    },
  },
});
